import axios from "axios";
import { PAEnvironment } from "../PA.Core/PAEnvironment";

const entityTypes = ["Customer", "Product", "Period"] as const;
export type EntityDimension = (typeof entityTypes)[number];

/** Check if a value is a valid EntityDimension */
export function IsEntityDimension(value: unknown): value is EntityDimension {
    if (typeof value !== "string") return false;
    return entityTypes.includes(value as EntityDimension);
}

/** Request to transform one entity type to another */
export type EntityTransformRequest = {
    /** The WB_Hierarchy_Id of the hierarchy to use for the transformation */
    wbhierarchyId?: number;
    /** The entity dimension to use for the transformation */
    entityDimension?: EntityDimension;
    /**) The TARGET_WB_ID of the hierarchy to use for the transformation */
    wbhierarchyTargetId?: number;
    /** The WB_Entity_ID of the source entity type */
    wbentityIdSource: number;
    /** The WB_Entity_ID of the target entity type */
    wbentityIdTarget: number;
    /** The sales org ID to use for the transformation */
    wbsalesOrgId: number;
    /** The effective date to use for the transformation */
    effectiveDate: string;
    /** The WB IDs of the source entities to transform */
    sourceWBIds: number[];
};

/** The WB ID of the relationship target, along with an allocation percentage from the source to the target */
export type EntityTransformAllocation = {
    wbId: number;
    allocationPct: number;
};

export type EntityTransformResponse = {
    /** Mapping of the requested source WB IDs to their relations. Includes an allocationPct */
    mappings: { [sourceWBID: number]: EntityTransformAllocation[] };
    /** Can be "Up", "Down", or "Flat" */
    direction: "Up" | "Down" | "Flat";
    errors?: string[];
    warnings?: string[];
    infos?: string[];
};

function isEntityTransformResponse(value: unknown): value is EntityTransformResponse {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    const { mappings, direction, errors, warnings, infos } = value as EntityTransformResponse;

    if (typeof mappings !== "object" || mappings === null) {
        return false;
    }
    if (typeof direction !== "string" || !["Up", "Down", "Flat"].includes(direction)) {
        return false;
    }
    if (errors !== undefined && !Array.isArray(errors)) {
        return false;
    }
    if (warnings !== undefined && !Array.isArray(warnings)) {
        return false;
    }
    if (infos !== undefined && !Array.isArray(infos)) {
        return false;
    }
    return true;
}

export type IHierarchyTransformService = {
    getEntityTransform(request: EntityTransformRequest): Promise<EntityTransformResponse>;
};

function createResponseWithError(...messages: string[]): EntityTransformResponse {
    return {
        mappings: {},
        direction: "Down",
        errors: messages,
    };
}

class HierarchyTransformService implements IHierarchyTransformService {
    async getEntityTransform(request: EntityTransformRequest): Promise<EntityTransformResponse> {
        // Implementation goes here
        return this.makeRequest(request);
    }

    private async makeRequest(request: EntityTransformRequest): Promise<EntityTransformResponse> {
        let sEndPoint = <string>PAEnvironment.getValue(PAEnvironment.Network_URL_Base, "/");
        if (sEndPoint.endsWith("/")) sEndPoint += "entityTransform";
        else sEndPoint += "/entityTransform";

        try {
            const resp = await axios.post(sEndPoint, { data: request });
            if (resp.status !== 200) {
                return createResponseWithError(`Server returned status ${resp.status}`);
            }
            if (!isEntityTransformResponse(resp.data)) {
                return createResponseWithError("Server returned invalid data: " + JSON.stringify(resp.data));
            }
            return resp.data;
        } catch (e: unknown) {
            console.error(
                `HierarchyTransformService: ${new Date().toUTCString()}: Unable to get entity transformation from server`,
                e,
            );
            if (e instanceof Error) {
                return createResponseWithError(e.message);
            }
            return createResponseWithError(`Unknown error: ${JSON.stringify(e)}`);
        }
    }
}

export default new HierarchyTransformService();
